/**
 * @file useWaiterReservations.ts
 * @description Hook managing live table bookings and party reservations.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../config/supabase.config';
import {
  fetchWaiterReservations,
  updateWaiterReservationStatus,
} from '../services/waiterReservations.service';
import type { WaiterReservation, WaiterReservationStatus } from '../../../types/reservation.types';

export const useWaiterReservations = (venueId: string | null) => {
  const [reservations, setReservations] = useState<WaiterReservation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastFetchedAtRef = useRef<number>(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadReservations = useCallback(async () => {
    if (!venueId) return;
    try {
      setError(null);
      const data = await fetchWaiterReservations(venueId);
      lastFetchedAtRef.current = Date.now();
      setReservations(data);
    } catch (err: any) {
      console.error('[useWaiterReservations] Failed to load reservations:', err);
      setError(err?.message || 'Failed to load reservations');
    } finally {
      setIsLoading(false);
    }
  }, [venueId]);

  const loadReservationsRef = useRef(loadReservations);
  loadReservationsRef.current = loadReservations;

  // Schedules a debounced refresh to avoid redundant rapid queries on burst changes
  const scheduleRefresh = useCallback((immediate?: boolean) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (immediate) {
      loadReservationsRef.current();
      return;
    }
    debounceTimerRef.current = setTimeout(() => {
      loadReservationsRef.current();
    }, 300);
  }, []);

  useEffect(() => {
    if (!venueId) return;

    loadReservationsRef.current();

    // Supabase Realtime channel for live floor reservation updates (event-driven, no polling)
    const channel = supabase
      .channel(`waiter-reservations-${venueId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reservations',
          filter: `venue_id=eq.${venueId}`,
        },
        (payload) => {
          // Optimistically update reservation status on direct mutation
          if (payload.eventType === 'UPDATE' && payload.new?.id) {
            const updated = payload.new as any;
            setReservations(prev =>
              prev.map(r => (r.id === updated.id ? { ...r, status: updated.status ?? r.status } : r))
            );
          }
          scheduleRefresh();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED' && Date.now() - lastFetchedAtRef.current > 30000) {
          scheduleRefresh(true);
        }
      });

    // Reconnection catch-up: refresh only when device reconnects or tab becomes visible after idle (>60s)
    const handleReconnection = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastFetchedAtRef.current > 60000) {
        scheduleRefresh(true);
      }
    };

    window.addEventListener('visibilitychange', handleReconnection);
    window.addEventListener('online', handleReconnection);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      window.removeEventListener('visibilitychange', handleReconnection);
      window.removeEventListener('online', handleReconnection);
      supabase.removeChannel(channel);
    };
  }, [venueId, scheduleRefresh]);

  const handleUpdateStatus = async (
    id: string,
    status: WaiterReservationStatus,
    reason?: string
  ) => {
    setReservations(prev =>
      prev.map(r => (r.id === id ? { ...r, status } : r))
    );
    try {
      await updateWaiterReservationStatus(id, status, reason);
      loadReservations();
    } catch (err) {
      console.error('[useWaiterReservations] Update status error:', err);
      loadReservations();
    }
  };

  // Derive counts for top bar badges and filter tabs
  const upcomingCount = reservations.filter(
    r => r.status === 'confirmed' || r.status === 'pending'
  ).length;

  const seatedCount = reservations.filter(r => r.status === 'seated').length;

  return {
    reservations,
    upcomingCount,
    seatedCount,
    isLoading,
    error,
    refreshReservations: loadReservations,
    handleUpdateStatus,
  };
};
